export const inferInventoryMode = (product) => {
  if (!product) return 'simple';

  const variantStock = product.variant_stock || [];
  const hasVariantColors = variantStock.some((variant) => variant.color?.trim());

  if (hasVariantColors || ((product.colors?.length || 0) > 0 && variantStock.length > 0)) {
    return 'color_size';
  }

  if ((product.sizes?.length || 0) > 0) {
    return 'size';
  }

  return 'simple';
};

export const getFilteredVariantStock = (product) => {
  if (!product?.variant_stock || !Array.isArray(product.variant_stock)) return [];

  const hasColors = product.variant_stock.some((variant) => variant.color?.trim());
  if (hasColors) {
    return product.variant_stock.filter((variant) => variant.color?.trim());
  }

  return product.variant_stock;
};

export const getUniqueColors = (product) => {
  const variantStock = getFilteredVariantStock(product);
  
  // First try to get colors from the colors array if available
  if (product.colors && product.colors.length > 0) {
    return [...new Set(product.colors.map(color => color?.trim()).filter(Boolean))];
  }
  
  // Fallback to variant stock colors
  const variantColors = variantStock.map((variant) => variant.color?.trim()).filter(Boolean);
  
  // Also check if there are any colors in color_images that aren't in variant_stock
  if (product.color_images && Object.keys(product.color_images).length > 0) {
    const imageColors = Object.keys(product.color_images).map(color => color?.trim()).filter(Boolean);
    return [...new Set([...variantColors, ...imageColors])];
  }
  
  return [...new Set(variantColors)];
};

export const getUniqueSizes = (product, inventoryMode, variantStock = []) => {
  if (inventoryMode === 'color_size') {
    return [...new Set(variantStock.map((variant) => variant.size).filter(Boolean))];
  }

  if (inventoryMode === 'size') {
    return product.sizes || [];
  }

  return [];
};

export const getProductStock = (product, inventoryMode, { selectedColor = '', selectedSize = '' } = {}) => {
  if (!product) return 0;

  // For combo products, use the calculated_stock from backend
  if (product.product_type === 'combo' || product.item_type === 'combo') {
    return Number(product.calculated_stock ?? product.stock ?? 0);
  }

  if (inventoryMode === 'color_size') {
    const variantStock = getFilteredVariantStock(product);

    if (variantStock.length === 0) {
      return Number(product.stock ?? 0);
    }

    if (selectedColor || selectedSize) {
      const matchingVariants = variantStock.filter((variant) =>
        (selectedColor ? variant.color === selectedColor : true) &&
        (selectedSize ? variant.size === selectedSize : true)
      );

      if (matchingVariants.length === 0) {
        return 0;
      }

      return matchingVariants.reduce((sum, variant) => sum + Number(variant.stock || 0), 0);
    }

    return variantStock.reduce((sum, variant) => sum + Number(variant.stock || 0), 0);
  }

  if (inventoryMode === 'size') {
    const sizeStock = product.size_stock || {};

    if (selectedSize) {
      return Number(sizeStock[selectedSize] ?? 0);
    }

    return Object.values(sizeStock).reduce((sum, stock) => sum + Number(stock || 0), 0);
  }

  return Number(product.stock ?? 0);
};

export const getProductImages = (product, inventoryMode, { selectedColor = '', uniqueColors = [] } = {}) => {
  if (!product) return ['/placeholder.svg'];

  if (inventoryMode === 'color_size') {
    if (uniqueColors.length > 0) {
      // Helper function to find color images with case-insensitive matching
      const findColorImages = (colorName) => {
        if (!colorName || !product.color_images) return null;
        
        // Try exact match first
        if (product.color_images[colorName]?.length > 0) {
          return product.color_images[colorName];
        }
        
        // Try case-insensitive match
        const normalizedSearch = colorName.trim().toLowerCase();
        const matchingKey = Object.keys(product.color_images).find(
          key => key.trim().toLowerCase() === normalizedSearch
        );
        
        if (matchingKey && product.color_images[matchingKey]?.length > 0) {
          return product.color_images[matchingKey];
        }
        
        return null;
      };

      // Try selected color first
      if (selectedColor) {
        const selectedColorImages = findColorImages(selectedColor);
        if (selectedColorImages) {
          return selectedColorImages;
        }
      }

      // Fallback to first available color
      if (uniqueColors.length > 0) {
        const firstColorImages = findColorImages(uniqueColors[0]);
        if (firstColorImages) {
          return firstColorImages;
        }
      }
      
      // If no color images found, try any color in color_images
      if (product.color_images && Object.keys(product.color_images).length > 0) {
        const firstAvailableColorImages = Object.values(product.color_images).find(images => images?.length > 0);
        if (firstAvailableColorImages) {
          return firstAvailableColorImages;
        }
      }
    }
  }

  if (product.images?.length > 0) {
    return product.images;
  }

  return ['/placeholder.svg'];
};
